import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const dashboardApi = createApi({
  reducerPath: "dashboardApi",
  tagTypes: ["RecentActivity", "ReviewDraft", "LocalRequests"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.NEXT_PUBLIC_BASE_URL}/dashboard/`,
    prepareHeaders: (headers) => {
      if (typeof document === "undefined") return headers;
      for (const cookie of document.cookie.split(";")) {
        const trimmed = cookie.trim();
        if (trimmed.startsWith("token=")) {
          headers.set(
            "authorization",
            `Bearer ${trimmed.slice("token=".length)}`
          );
          break;
        }
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    forYou: builder.query({
      query: ({ limit = 10 } = {}) => ({
        url: `for-you?limit=${encodeURIComponent(limit)}`,
        method: "GET",
        // Do not leave the dashboard skeleton pending indefinitely when the
        // recommendation service (or its database) stops responding. RTK
        // Query aborts this request and exposes TIMEOUT_ERROR to ForYouRow,
        // where the existing error state offers an explicit fresh retry.
        timeout: 12_000,
      }),
    }),
    recentActivity: builder.query({
      query: ({ limit = 8 } = {}) => ({
        url: `recent-activity?limit=${limit}`,
        method: "GET",
      }),
      providesTags: ["RecentActivity"],
    }),
    localRequests: builder.query({
      query: ({ days = 7, page = 1, limit = 3 } = {}) => ({
        url: `local-requests?days=${days}&page=${page}&limit=${limit}`,
        method: "GET",
      }),
      providesTags: ["LocalRequests"],
    }),
    reviewDraft: builder.query({
      query: ({ targetType, targetKey }) => ({
        url: `review-drafts/${targetType}/${encodeURIComponent(targetKey)}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { targetType, targetKey }) => [
        { type: "ReviewDraft", id: `${targetType}:${targetKey}` },
      ],
    }),
    saveReviewDraft: builder.mutation({
      query: ({ targetType, targetKey, ...body }) => ({
        url: `review-drafts/${targetType}/${encodeURIComponent(targetKey)}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { targetType, targetKey }) => [
        "RecentActivity",
        { type: "ReviewDraft", id: `${targetType}:${targetKey}` },
      ],
    }),
    deleteReviewDraft: builder.mutation({
      query: ({ targetType, targetKey }) => ({
        url: `review-drafts/${targetType}/${encodeURIComponent(targetKey)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { targetType, targetKey }) => [
        "RecentActivity",
        { type: "ReviewDraft", id: `${targetType}:${targetKey}` },
      ],
    }),
  }),
});

export const {
  useForYouQuery,
  useRecentActivityQuery,
  useLocalRequestsQuery,
  useReviewDraftQuery,
  useSaveReviewDraftMutation,
  useDeleteReviewDraftMutation,
} = dashboardApi;
